/**
 * Email Classifier Step — Uses secure backend API for email classification
 *
 * Analyzes email content and classifies it as:
 * - 'work' — Work-related email
 * - 'advertisement' — Marketing/promotional
 * - 'spam' — Spam/unwanted
 * - 'personal' — Personal email
 *
 * Also extracts sender info for client matching.
 *
 * Config:
 * - subject: string — Email subject
 * - body: string — Email body text
 * - from: string — Sender email address
 * - fromName?: string — Sender display name
 *
 * Uses the secure backend GraphQL endpoint which has API keys stored server-side.
 * This approach is secure because:
 * 1. API keys are never exposed to the client
 * 2. All AI calls go through the backend
 * 3. PII sanitization happens on the server
 */

import type { EngineStep, EngineExecutionContext, StepExecutionResult, StepExecutor } from '../types';
import { getApiEndpoints } from '../../../config/appConfig';

export type EmailClassification = 'work' | 'advertisement' | 'spam' | 'personal' | 'unknown';

export interface EmailClassificationResult {
  classification: EmailClassification;
  confidence: number;
  reason: string;
  senderEmail: string;
  senderName: string;
  senderDomain: string;
  suggestedCompanyName?: string;
}

export const emailClassifierStep: StepExecutor = {
  type: 'email_classify',
  async execute(step: EngineStep, context: EngineExecutionContext): Promise<StepExecutionResult> {
    console.log('[EmailClassifier] === STEP STARTED ===');
    console.log('[EmailClassifier] Step config:', JSON.stringify(step.config, null, 2));

    try {
      const subject = (step.config.subject as string) || '';
      const body = (step.config.body as string) || '';
      const from = (step.config.from as string) || '';
      const fromName = (step.config.fromName as string) || '';

      console.log('[EmailClassifier] Extracted values:', { subject, from, fromName, bodyLength: body.length });

      if (!from) {
        return { success: false, error: 'Email "from" address is required' };
      }

      // Extract domain from email
      const emailMatch = from.match(/@([^@\s]+)$/);
      const senderDomain = emailMatch ? emailMatch[1].toLowerCase() : '';

      // Call the secure backend GraphQL endpoint for classification
      const endpoints = getApiEndpoints();
      const graphqlUrl = endpoints.graphql;

      console.log('[EmailClassifier] Calling secure backend API at:', graphqlUrl);
      console.log('[EmailClassifier] Email data:', { subject, from, fromName, bodyLength: body.length });

      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation ClassifyEmail($input: ClassifyEmailInput!) {
              classifyEmail(input: $input) {
                success
                classification
                confidence
                reason
                suggestedCompanyName
                error
              }
            }
          `,
          variables: {
            input: {
              subject,
              body: body.substring(0, 2000), // Limit body length
              from,
              fromName: fromName || null,
            },
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[EmailClassifier] Backend API error:', response.status, errorText);
        throw new Error(`Backend API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors && data.errors.length > 0) {
        console.error('[EmailClassifier] GraphQL errors:', data.errors);
        throw new Error(data.errors[0].message || 'GraphQL error');
      }

      const classifyResult = data.data?.classifyEmail;

      if (!classifyResult) {
        console.warn('[EmailClassifier] No classification result from backend');
        return {
          success: true,
          output: {
            emailClassification: {
              classification: 'unknown' as EmailClassification,
              confidence: 0,
              reason: 'Nessuna risposta dal server',
              senderEmail: from,
              senderName: fromName || from.split('@')[0],
              senderDomain,
            },
            isWorkEmail: false,
            isSpam: false,
            isAdvertisement: false,
          },
        };
      }

      if (!classifyResult.success) {
        console.warn('[EmailClassifier] Classification failed:', classifyResult.error);
      }

      console.log('[EmailClassifier] Backend classification result:', classifyResult);

      const result: EmailClassificationResult = {
        classification: (classifyResult.classification || 'unknown') as EmailClassification,
        confidence: classifyResult.confidence || 0,
        reason: classifyResult.reason || '',
        senderEmail: from,
        senderName: fromName || from.split('@')[0],
        senderDomain,
        suggestedCompanyName: classifyResult.suggestedCompanyName || undefined,
      };

      return {
        success: true,
        output: {
          emailClassification: result,
          isWorkEmail: result.classification === 'work',
          isSpam: result.classification === 'spam',
          isAdvertisement: result.classification === 'advertisement',
        },
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Email classification failed';
      console.error('[EmailClassifier] Error:', errorMsg);
      console.error('[EmailClassifier] Full error:', err);
      return {
        success: false,
        error: `Classification failed: ${errorMsg}`,
      };
    }
  },
};
